import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Button,
  Grid,
  Chip
} from "@mui/material";
import { getNewUrlsSummaryApi, getNewUrlsRawApi, addExcludedUrlApi } from "../../redux/api/clicksApi";
import { message } from "antd";

const ExpandableCell = ({ text }) => {
  const [expanded, setExpanded] = useState(false);
  
  if (!text) return null;
  
  return (
    <div 
      onClick={() => setExpanded(!expanded)}
      style={{ 
        cursor: 'pointer',
        maxWidth: '300px',
        wordWrap: 'break-word',
        display: '-webkit-box',
        WebkitLineClamp: expanded ? 'unset' : 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden'
      }}
      title={expanded ? "Click to collapse" : "Click to expand"}
    >
      {text}
    </div>
  );
};

const NewClicks = () => {
  const [timeframe, setTimeframe] = useState("1h");
  const [summaryData, setSummaryData] = useState([]);
  const [rawData, setRawData] = useState([]);
  const [page, setPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [inputPage, setInputPage] = useState("1");
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [loadingRaw, setLoadingRaw] = useState(false);
  const [selectedHostname, setSelectedHostname] = useState(null);

  const fetchSummary = async () => {
    setLoadingSummary(true);
    try {
      const res = await getNewUrlsSummaryApi({ timeframe });
      if (res.data?.code === 200) {
        setSummaryData(res.data.data);
      }
    } catch (err) {
      console.error(err);
    }
    setLoadingSummary(false);
  };

  const fetchRaw = async (p = page, hostnameFilter = selectedHostname) => {
    setLoadingRaw(true);
    try {
      const res = await getNewUrlsRawApi({ timeframe, page: p, limit: 25, hostname: hostnameFilter });
      if (res.data?.code === 200) {
        setRawData(res.data.data);
        setHasNextPage(res.data.hasNextPage);
        setPage(res.data.currentPage);
        setInputPage(String(res.data.currentPage));
      }
    } catch (err) {
      console.error(err);
    }
    setLoadingRaw(false);
  };

  useEffect(() => {
    fetchSummary();
    setPage(1);
    setInputPage("1");
    fetchRaw(1, selectedHostname);
  }, [timeframe]);

  const handleManualPageSubmit = (e) => {
    e.preventDefault();
    const p = parseInt(inputPage, 10);
    if (!isNaN(p) && p > 0) {
      fetchRaw(p, selectedHostname);
    } else {
      setInputPage(String(page));
    }
  };

  const handleHostnameClick = (hostname) => {
    setSelectedHostname(hostname);
    setPage(1);
    setInputPage("1");
    fetchRaw(1, hostname);
  };

  const clearHostnameFilter = () => {
    setSelectedHostname(null);
    setPage(1);
    setInputPage("1");
    fetchRaw(1, null);
  };

  const handleExcludeHostname = async (hostname) => {
    try {
      const res = await addExcludedUrlApi({ excl_match: hostname });
      if (res.data?.code === 200) {
        message.success(`Excluded ${hostname} successfully`);
      } else {
        message.error(res.data?.failed || "Failed to exclude");
      }
    } catch (err) {
      message.error("Failed to exclude hostname");
    }
  };

  return (
    <Box sx={{ padding: "20px" }}>
      <Typography variant="h4" sx={{ mb: 3 }}>
        New Merchants Data
      </Typography>

      <Box sx={{ mb: 3 }}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel id="timeframe-label">Timeframe</InputLabel>
          <Select
            labelId="timeframe-label"
            value={timeframe}
            label="Timeframe"
            onChange={(e) => setTimeframe(e.target.value)}
          >
            <MenuItem value="1h">Last 1 Hour</MenuItem>
            <MenuItem value="24h">Last 1 Day</MenuItem>
            <MenuItem value="3d">Last 3 Days</MenuItem>
            <MenuItem value="7d">Last 7 Days</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Summary Table */}
      <Typography variant="h6" sx={{ mb: 2 }}>
        Summary
      </Typography>
      <TableContainer component={Paper} sx={{ mb: 4, maxHeight: 400 }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell><strong>Hostname</strong> (Click to filter raw data)</TableCell>
              <TableCell align="right"><strong># of Clicks</strong></TableCell>
              <TableCell align="center"><strong>Action</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loadingSummary ? (
              <TableRow>
                <TableCell colSpan={2} align="center">Loading...</TableCell>
              </TableRow>
            ) : summaryData.length > 0 ? (
              summaryData.map((row, i) => (
                <TableRow key={i} hover>
                  <TableCell>
                    <span 
                      style={{ cursor: "pointer", color: "#1976d2", textDecoration: "underline" }}
                      onClick={() => handleHostnameClick(row.hostname)}
                    >
                      {row.hostname}
                    </span>
                  </TableCell>
                  <TableCell align="right">{row.clicks}</TableCell>
                  <TableCell align="center">
                    <Button 
                      variant="outlined" 
                      color="error" 
                      size="small" 
                      onClick={() => handleExcludeHostname(row.hostname)}
                    >
                      Exclude
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={3} align="center">No data found</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Raw Data Table */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
        <Typography variant="h6">
          Raw Data (25 Rows per page)
        </Typography>
        {selectedHostname && (
          <Chip 
            label={`Filtered by: ${selectedHostname}`} 
            onDelete={clearHostnameFilter} 
            color="primary"
            variant="outlined"
          />
        )}
      </Box>
      <TableContainer component={Paper} sx={{ mb: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>ID</strong></TableCell>
              <TableCell><strong>Time</strong></TableCell>
              <TableCell><strong>Hostname</strong></TableCell>
              <TableCell><strong>URL</strong></TableCell>
              <TableCell><strong>IP</strong></TableCell>
              <TableCell><strong>Tag</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loadingRaw ? (
              <TableRow>
                <TableCell colSpan={6} align="center">Loading...</TableCell>
              </TableRow>
            ) : rawData.length > 0 ? (
              rawData.map((row) => (
                <TableRow key={row.unique_id} hover>
                  <TableCell><ExpandableCell text={row.unique_id} /></TableCell>
                  <TableCell>{new Date(row.time).toLocaleString()}</TableCell>
                  <TableCell>{row.hostname}</TableCell>
                  <TableCell><ExpandableCell text={row.url} /></TableCell>
                  <TableCell>{row.ip}</TableCell>
                  <TableCell>{row.tag}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center">No data available</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination Controls */}
      <Grid container spacing={2} alignItems="center" justifyContent="center">
        <Grid item>
          <Button 
            variant="contained" 
            disabled={page <= 1} 
            onClick={() => fetchRaw(page - 1, selectedHostname)}
            size="small"
          >
            Previous
          </Button>
        </Grid>
        
        <Grid item>
          <Typography variant="body2">Page {page}</Typography>
        </Grid>

        <Grid item>
          <Button 
            variant="contained" 
            disabled={!hasNextPage} 
            onClick={() => fetchRaw(page + 1, selectedHostname)}
            size="small"
          >
            Next
          </Button>
        </Grid>

        <Grid item sx={{ ml: 3 }}>
          <form onSubmit={handleManualPageSubmit} style={{ display: 'flex', alignItems: 'center' }}>
            <TextField
              size="small"
              value={inputPage}
              onChange={(e) => setInputPage(e.target.value)}
              sx={{ width: 70, mr: 1 }}
              type="number"
              inputProps={{ min: 1 }}
            />
            <Button type="submit" variant="outlined" size="small">
              Go
            </Button>
          </form>
        </Grid>
      </Grid>
    </Box>
  );
};

export default NewClicks;
